// Per-firm widget theme. Mirrors apps/dashboard/lib/widget-theme.ts and the
// chat-service /chat/config endpoint. Fetched at mount so branding is
// data-driven — a firm restyles in Settings and the widget picks it up on the
// next page load, with no re-embed.
//
// Curated tokens only — no raw CSS — so the surface stays safe to support but
// still gives firms far more control than typical competitor widgets:
// position, size, accent color, light/dark surface, avatar, launcher style,
// configurable trust chips + starter prompts, and one-click presets.

export type WidgetIcon = "headset" | "chat" | "scales" | "sparkles";
export type CornerStyle = "rounded" | "sharp" | "pill";
export type LauncherPosition = "br" | "bl";
export type LauncherSize = "standard" | "compact";
export type WidgetScheme = "light" | "dark";

export interface WidgetTheme {
  primary_color: string;
  /** Secondary brand color for the primary CTA + send button + links. Falls
   *  back to primary_color when blank, so existing firms are unaffected. */
  accent_color: string | null;
  launcher_label: string;
  header_title: string | null;
  icon: WidgetIcon;
  logo_url: string | null;
  /** Optional rep/headshot image shown in the header + welcome hero. */
  avatar_url: string | null;
  corner_style: CornerStyle;
  /** Which corner the floating launcher sits in. */
  launcher_position: LauncherPosition;
  /** Launcher footprint — "compact" hides the label until hover (icon-only). */
  launcher_size: LauncherSize;
  /** Surface theme of the chat window. */
  scheme: WidgetScheme;
  rep_name: string;
  // Welcome/start screen copy. null title → templated default.
  welcome_title: string | null;
  welcome_subtitle: string;
  /** Up to 3 short trust badges on the welcome screen. Empty → hidden. */
  trust_chips: string[];
  /** Up to 4 tappable starter prompts on the welcome screen. Empty → hidden. */
  starter_prompts: string[];
  /** Proactive teaser shown from the launcher a few seconds after load. */
  nudge_message: string;
  /** Master on/off for the proactive nudge (independent of the message text). */
  nudge_enabled: boolean;
  /** Intent gate: show the pre-chat form (name/phone/inquiry) before opening
   *  a chat. Server-merged from firms.config.intake.prechat.enabled; default
   *  on. False → legacy flow (Start chat goes straight to the greeting). */
  prechat_enabled: boolean;
  /** Pre-chat form intro line. Empty → the widget's built-in neutral default.
   *  Served via /chat/config (additive; old servers simply omit it). */
  form_intro: string;
}

export const DEFAULT_THEME: WidgetTheme = {
  primary_color: "#14315c",
  accent_color: null,
  // Practice-neutral defaults: the platform serves every practice area, so
  // nothing here may read injury-only. A firm can still brand it PI in Settings.
  launcher_label: "Legal help? Chat with us",
  header_title: null,
  icon: "headset",
  logo_url: null,
  avatar_url: null,
  corner_style: "rounded",
  launcher_position: "br",
  launcher_size: "standard",
  scheme: "light",
  rep_name: "Jas",
  welcome_title: null,
  welcome_subtitle: "", // empty → widget uses its neutral templated default
  trust_chips: [],
  starter_prompts: [],
  nudge_message: "Legal questions? I'm here to help.",
  nudge_enabled: true,
  prechat_enabled: true,
  form_intro: "", // empty → widget uses its neutral built-in default
};

// A coherent radius scale per corner_style, so the firm's choice cascades to
// every element (not just the window) and all corners feel intentional.
const CORNER_RADII: Record<CornerStyle, { lg: number; md: number; sm: number; bubble: number }> = {
  sharp: { lg: 10, md: 8, sm: 6, bubble: 10 },
  rounded: { lg: 16, md: 12, sm: 8, bubble: 18 },
  pill: { lg: 22, md: 16, sm: 12, bubble: 22 },
};

// Black/white text that stays legible on a brand color — real WCAG relative
// luminance + contrast-ratio comparison (not YIQ), so mid-tone brands (e.g. a
// teal) correctly get dark text instead of failing white-on-color.
export function readableOn(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return "#ffffff";
  const n = parseInt(m[1]!, 16);
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  const contrastWhite = 1.05 / (L + 0.05);
  const contrastBlack = (L + 0.05) / 0.05;
  return contrastWhite >= contrastBlack ? "#ffffff" : "#0f172a";
}

// Surface palette for the window chrome, driven by `scheme`. Targets the EXACT
// CSS variable names the widget stylesheet already consumes (--acc-bg,
// --acc-bg-soft, --acc-text, --acc-text-muted, --acc-border) so switching to
// dark restyles the entire subtree — header excluded (it follows --acc-primary).
function surfaceVars(scheme: WidgetScheme): Record<string, string> {
  if (scheme === "dark") {
    return {
      "--acc-bg": "#0f1722",
      "--acc-bg-soft": "#162130",
      "--acc-text": "#e8edf4",
      "--acc-text-muted": "#9fb0c3",
      "--acc-border": "#243345",
      "--acc-bot-bubble": "#1b2737",
      // Lighter red so the error surface stays legible on the dark panel.
      "--acc-error": "#f87171",
    };
  }
  return {
    "--acc-bg": "#ffffff",
    "--acc-bg-soft": "#f7f8fa",
    "--acc-text": "#1f2937",
    "--acc-text-muted": "#6b7280",
    "--acc-border": "#e5e7eb",
    "--acc-bot-bubble": "#ffffff",
    "--acc-error": "#b91c1c",
  };
}

// CSS custom properties to set on the widget root so the whole subtree
// (launcher, header, buttons, bubbles) follows the firm's brand.
export function themeCssVars(theme: WidgetTheme): Record<string, string> {
  const accent = theme.accent_color && /^#[0-9a-fA-F]{6}$/.test(theme.accent_color)
    ? theme.accent_color
    : theme.primary_color;
  const r = CORNER_RADII[theme.corner_style];
  return {
    "--acc-primary": theme.primary_color,
    "--acc-accent": accent,
    "--acc-on-brand": readableOn(theme.primary_color),
    "--acc-on-accent": readableOn(accent),
    "--acc-radius": `${r.lg}px`,
    "--acc-radius-md": `${r.md}px`,
    "--acc-radius-sm": `${r.sm}px`,
    "--acc-radius-bubble": `${r.bubble}px`,
    ...surfaceVars(theme.scheme),
  };
}

// One-click style presets the dashboard editor can apply. Each is a partial
// theme that the firm can then fine-tune. Curated for instant credibility.
export const WIDGET_PRESETS: Record<string, Partial<WidgetTheme>> = {
  professional: {
    primary_color: "#14315c",
    accent_color: "#2563eb",
    corner_style: "rounded",
    scheme: "light",
    icon: "scales",
  },
  midnight: {
    primary_color: "#0b1220",
    accent_color: "#6366f1",
    corner_style: "rounded",
    scheme: "dark",
    icon: "sparkles",
  },
  warm: {
    primary_color: "#7c2d12",
    accent_color: "#ea580c",
    corner_style: "pill",
    scheme: "light",
    icon: "chat",
  },
  emerald: {
    primary_color: "#064e3b",
    accent_color: "#059669",
    corner_style: "rounded",
    scheme: "light",
    icon: "headset",
  },
};

// Fetch a firm's theme. Never throws — branding must never block the widget.
// Returns null on any network/HTTP/parse failure so callers can tell "fetch
// failed" apart from "firm uses the defaults" (a failure must keep whatever
// theme is already showing and must never be written to the cache).
export async function fetchTheme(chatHost: string, firmName: string, firmToken?: string): Promise<WidgetTheme | null> {
  try {
    const query = firmToken
      ? `firm_token=${encodeURIComponent(firmToken)}`
      : `firm_name=${encodeURIComponent(firmName)}`;
    const url = `${chatHost.replace(/\/$/, "")}/chat/config?${query}`;
    const r = await fetch(url, { method: "GET" });
    if (!r.ok) return null;
    const j = (await r.json()) as { theme?: Partial<WidgetTheme> };
    return { ...DEFAULT_THEME, ...(j.theme ?? {}) };
  } catch {
    return null;
  }
}

// ---- First-paint theme cache ------------------------------------------------
// The theme is fetched after the first render, so without a cache every page
// load painted the default navy launcher before snapping to the firm's brand.
// The cache holds the last successfully fetched theme so a repeat visit paints
// correctly on the very first frame. No TTL and no version stamp per entry:
// a fresh fetch always runs and overwrites the cache, so a dashboard save takes
// effect on the next page load regardless of how old the entry is.

const THEME_CACHE_V = 1;
const themeCacheKey = (firmKey: string) => `acceltra_theme:${firmKey}`;

export function loadCachedTheme(firmKey: string): WidgetTheme | null {
  try {
    const raw = localStorage.getItem(themeCacheKey(firmKey));
    if (!raw) return null;
    const p = JSON.parse(raw) as { v?: number; theme?: unknown };
    if (!p || p.v !== THEME_CACHE_V || typeof p.theme !== "object" || p.theme === null) return null;
    // Merge over defaults so fields shipped after the entry was written get
    // their defaults instead of arriving undefined.
    return { ...DEFAULT_THEME, ...(p.theme as Partial<WidgetTheme>) };
  } catch {
    return null; // blocked storage / corrupt JSON — behave like a first visit
  }
}

export function saveCachedTheme(firmKey: string, theme: WidgetTheme): void {
  try {
    localStorage.setItem(themeCacheKey(firmKey), JSON.stringify({ v: THEME_CACHE_V, theme }));
  } catch {
    /* storage full / disabled — non-fatal */
  }
}
